// client/src/services/pto.ts
// API client for the PTO Dashboard.

import type { PTOBootstrapData, PTOEntryInput } from '../pages/PTO/ptoTypes';

async function api<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...opts });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
  return res.json();
}

function post<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// ── Bootstrap ────────────────────────────────────────────────────────────────

export function fetchPTOBootstrap(): Promise<PTOBootstrapData> {
  return api<PTOBootstrapData>('/api/pto-bootstrap');
}

// ── Entries ──────────────────────────────────────────────────────────────────

export function createEntry(input: PTOEntryInput): Promise<{ id: string }> {
  return post('/api/pto-entry', input);
}

export function updateEntry(id: string, input: Partial<PTOEntryInput>): Promise<void> {
  return post('/api/pto-entry-update', { id, ...input });
}

export function deleteEntry(id: string): Promise<void> {
  return post('/api/pto-entry-delete', { id });
}

// ── Front Desk ──────────────────────────────────────────────────────────────

export function upsertFrontDesk(payload: {
  id?: number;            // present = edit that row; absent = insert a new row
  office: string;
  coverageDate: string;
  staffIndex: string;
  timeIn?: string | null;
  timeOut?: string | null;
  note?: string | null;
  isNoCoverage?: boolean;
}): Promise<{ id: number }> {
  return post('/api/pto-front-desk', payload);
}

export function deleteFrontDesk(id: number): Promise<void> {
  return post('/api/pto-front-desk-delete', { id });
}

// ── Phone Duty ──────────────────────────────────────────────────────────────

export function upsertPhoneDuty(payload: {
  dutyDate: string;
  staffIndex: string;
  timeIn?: string | null;
  timeOut?: string | null;
}): Promise<{ id: number }> {
  return post('/api/pto-phone-duty', payload);
}

export function deletePhoneDuty(id: number): Promise<void> {
  return post('/api/pto-phone-duty-delete', { id });
}
